import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { WebApiResponseBase } from '@app/shared/classes/general';
import { DatepickerNormalFormat, NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Driver } from '@shared/classes/driver';
import { DriverSearchGridComponent } from '@app/shared/components/driver-search-grid/driver-search-grid.component';
import { Award } from '@app/shared/classes/award';

@Component({
  selector: 'app-award-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, DriverSearchGridComponent],
  templateUrl: './award-edit-modal-dialog.component.html',
  styleUrl: './award-edit-modal-dialog.component.css',
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerNormalFormat },
  ],
})
export class AwardEditModalDialogComponent extends EditModalDialogBasedComponent<AwardEditModalDialogComponent, Award> {
  formPath = NgxsFormPath.AWARD_EDIT_FORM;
  title = "";

  driverList$: Observable<Driver[]>;
  driverApexIDOrNameFilterSub = new BehaviorSubject<string>('');
  driverApexIDOrNameFilter$ = this.driverApexIDOrNameFilterSub.asObservable();

  @Select(AppDataState.taskExecuting(NgxsActionName.UPDATE_AWARD)) processing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.UPDATE_AWARD)) response$: Observable<WebApiResponseBase>;

  constructor(public dialogRef: MatDialogRef<AwardEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<Award>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<Award>) => {
    if (data.isCreate) {
      this.editForm = this.formBuilder.group({
        award_Id: -1,
        driver_Id: -1,
        apexId: ["", Validators.required],
        driverName: ["", Validators.required],
        awardName: ["", Validators.required],
        awardDate: ["", Validators.required]
      });
    }
    else {
      this.editForm = this.formBuilder.group({
        award_Id: data.data.award_Id,
        apexId: [data.data.driver.apexId, Validators.required],
        driver_Id: [data.data.driver_Id, Validators.required],
        driverName: [data.data.driverName, Validators.required],
        awardName: [data.data.awardname, Validators.required],
        awardDate: [data.data.awardDate, Validators.required],
      });
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateAward), withLatestFrom(this.response$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));

  }

  onDriverApexIDOrNameKeyUp = (event: any) => {
    this.driverApexIDOrNameFilterSub.next(event.target.value as string);
  }

  onDriverItemClicked = (event: any) => {
    var currentValue = { driver_Id: -1, apexId: '', driverName: '' };
    if (event.toggled) {
      var driver = event.item as Driver;
      currentValue.driver_Id = driver.driver_Id;
      currentValue.apexId = driver.apexId;
      currentValue.driverName = driver.fullName;
      this.driverApexIDOrNameFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateAward());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.driverApexIDOrNameFilterSub.complete();
  }
}
